import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import UserToClientModalPicker from "components/Picker/UserToClientPicker/UserToClientModalPicker";
import React from "react";
import { Message } from "semantic-ui-react";
import { routes } from "utilities/routing/generatedRoutes";
import { pluralize } from "../../../helpers/translation";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { ButtonPrimary } from "components/Atoms/Button";

interface Props {
  stats: server.dto.UserToClientUsage
  clientConfig: server.dto.ClientConfiguration
  sourceUserToClient: server.dto.UserForInternalAdmin
}

interface TransferDropdownProps {
  currentUserToClientId: number
  value: server.dto.UserToClientSmall
  onChange: (newUser: server.dto.UserToClientSmall) => void
}

const TransferDropdown = ({ currentUserToClientId, value, onChange }: TransferDropdownProps) => <div className="main-section-template-fields-padding">
  <UserToClientModalPicker
    value={value}
    onChange={onChange}
    excludeUsers={[currentUserToClientId]}
    required={false}
    canCreateNew={false}
    validatorKey="userToClient"
  />
</div>;

const TransferUserData = (props: Props) => {
  const { stats, clientConfig } = props;
  const [companyOwner, setCompanyOwner] = React.useState<server.dto.UserToClientSmall>(undefined);
  const [contactOwner, setContactOwner] = React.useState<server.dto.UserToClientSmall>(undefined);
  const [showOwner, setShowOwner] = React.useState<server.dto.UserToClientSmall>(undefined);
  const [quotesOwner, setQuotesOwner] = React.useState<server.dto.UserToClientSmall>(undefined);
  const [purchaseAgreementsOwner, setPurchaseAgreementsOwner] = React.useState<server.dto.UserToClientSmall>(undefined);
  const [contractsOwner, setContractsOwner] = React.useState<server.dto.UserToClientSmall>(undefined);
  const [invoiceOwner, setInvoiceOwner] = React.useState<server.dto.UserToClientSmall>(undefined);

  const [taskOwner, setTaskOwner] = React.useState<server.dto.UserToClientSmall>(undefined);

  const selectedAnyOwner = [companyOwner, contactOwner, showOwner, quotesOwner, purchaseAgreementsOwner, contractsOwner, invoiceOwner, taskOwner]
    .some(owner => owner != null);
  const hasAnyDataToTransfer = Object.values(stats).some(count => count > 0);

  const submit = () => window.CoreApi.UserToClient.TransferUserToClientUsage({
    sourceUserToClientId: props.sourceUserToClient.userToClientId,
    companiesTargetUserToClientId: companyOwner?.userToClientId,
    contactsTargetUserToClientId: contactOwner?.userToClientId,
    showsTargetUserToClientId: showOwner?.userToClientId,
    quotesTargetUserToClientId: quotesOwner?.userToClientId,
    purchaseAgreementsTargetUserToClientId: purchaseAgreementsOwner?.userToClientId,
    contractsTargetUserToClientId: contractsOwner?.userToClientId,
    invoicesTargetUserToClientId: invoiceOwner?.userToClientId,
    tasksTargetUserToClientId: taskOwner?.userToClientId
  });

  if (!hasAnyDataToTransfer) {
    return <DetailSectionTemplate
      isInPageEditable={false}
      onValueChanged={() => { }}
      sections={[{
        key: "owner",
        type: "regular",
        title: RESX.GeneralLabels.Owner,
        fields: [{
          key: "title",
          label: RESX.GeneralLabels.Name,
          render: () => <div>
            {props.sourceUserToClient.user.fullName}
          </div>
        }, {
          key: "noData",
          label: "",
          hideLabel: true,
          render: () => <Message color="blue">
            {RESX.GeneralLabels.NoResultsFound}
          </Message>
        }, {
          key: "backToDetail",
          label: "",
          hideLabel: true,
          render: () => <div className="flex justify-end">
            <ButtonPrimary
              label={RESX.Buttons.DetailView}
              href={routes.internalUser_Detail(props.sourceUserToClient.userToClientId).absolute}
            />
          </div>
        }]
      }]}
    />
  }

  return <EditSectionTemplate
    submit={submit}
    redirectUrl={() => routes.internalUser_Tansfer(props.sourceUserToClient.userToClientId).absolute}
    saveButtonLabel={RESX.Buttons.Transfer}
    saveButtonDisabled={!selectedAnyOwner}
    sections={[{
      key: "owner",
      type: "regular",
      title: RESX.GeneralLabels.Owner,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.Name,
        renderEditNew: () => <div className="main-section-template-fields-padding">
          {props.sourceUserToClient.user.fullName}
        </div>
      }]
    }, {
      key: "transferToOtherUser",
      type: "regular",
      title: RESX.SoUser.TransferToOtherUser,
      fields: [{
        key: "ownedCompanies",
        hidden: stats.ownedCompanies === 0,
        label: pluralize(stats.ownedCompanies, RESX.Company.resxCompany, RESX.Company.Companies),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={companyOwner}
          onChange={setCompanyOwner}
        />
      }, {
        key: "ownedContacts",
        hidden: stats.ownedContacts === 0,
        label: pluralize(stats.ownedContacts, RESX.Contact.resxContact, RESX.Contact.Contacts),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={contactOwner}
          onChange={setContactOwner}
        />
      }, {
        key: "ownedShows",
        hidden: stats.ownedShows === 0,
        label: pluralize(stats.ownedShows, site.currentEnvironment.show.singular, site.currentEnvironment.show.plural),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={showOwner}
          onChange={setShowOwner}
        />
      }, {
        key: "ownedQuotes",
        hidden: stats.ownedQuotes === 0,
        label: pluralize(stats.ownedQuotes, clientConfig.quote.singular, clientConfig.quote.plural),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={quotesOwner}
          onChange={setQuotesOwner}
        />
      }, {
        key: "ownedPurchaseAgreements",
        hidden: stats.ownedPurchaseAgreements === 0,
        label: pluralize(stats.ownedPurchaseAgreements, clientConfig.purchaseAgreement.singular, clientConfig.purchaseAgreement.plural),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={purchaseAgreementsOwner}
          onChange={setPurchaseAgreementsOwner}
        />
      }, {
        key: "ownedContracts",
        hidden: stats.ownedContracts === 0,
        label: pluralize(stats.ownedContracts, clientConfig.contract.singular, clientConfig.contract.plural),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={contractsOwner}
          onChange={setContractsOwner}
        />
      }, {
        key: "ownedInvoices",
        hidden: stats.ownedInvoices === 0,
        label: pluralize(stats.ownedInvoices, clientConfig.invoice.singular, clientConfig.invoice.plural),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={invoiceOwner}
          onChange={setInvoiceOwner}
        />
      }, {
        key: "assignedTasks",
        hidden: (stats.expiredAssignedTasks + stats.futureAssignedTasks) === 0,
        label: pluralize(stats.expiredAssignedTasks + stats.futureAssignedTasks, RESX.Task.resxTask, RESX.Task.Tasks),
        renderEditNew: () => <TransferDropdown
          currentUserToClientId={props.sourceUserToClient.userToClientId} value={taskOwner}
          onChange={setTaskOwner}
        />
      }, {
        key: "warning",
        hidden: !selectedAnyOwner,
        label: "",
        hideLabel: true,
        renderEditNew: () => <Message color="red">
          {RESX.GeneralWarnings.ThisActionCannotBeUndone}
        </Message>
      }],
    }]}
  />
};

export default TransferUserData;